'use client';

import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface DestinationSectionProps {
  className?: string;
}

const DestinationSection = ({ className = '' }: DestinationSectionProps) => {
  const destinations = [
  {
    id: 1,
    country: "Germany",
    description: "Tuition-free public universities | TUM #37 globally (QS 2025)",
    image: "https://images.unsplash.com/photo-1562282553-d37a3b2e0581",
    alt: "Brandenburg Gate in Berlin with blue sky and colorful surroundings",
    universities: "120+",
    students: "8,500+"
  },
  {
    id: 10,
    country: "Dubai",
    description: "Tax-free income | 70+ accredited institutions | Golden Visa",
    image: "https://images.unsplash.com/photo-1708800843962-3b30b9f25ee1",
    alt: "Dubai skyline with Burj Khalifa and modern skyscrapers glowing in vibrant golden sunset",
    universities: "35+",
    students: "4,100+"
  },
  {
    id: 2,
    country: "Singapore",
    description: "NUS #8 & NTU #15 globally | MOE Tuition Grant up to 90%",
    image: "https://images.unsplash.com/photo-1562765138-ee8628ec52b3",
    alt: "Marina Bay Sands hotel with Singapore colorful skyline at night",
    universities: "30+",
    students: "3,200+"
  },
  {
    id: 3,
    country: "France",
    description: "Public tuition from €170/year | 1,700+ English programs",
    image: "https://images.unsplash.com/photo-1684609579901-67c14fc120b1",
    alt: "Eiffel Tower in Paris with colorful gardens and blue sky",
    universities: "85+",
    students: "6,200+"
  },
  {
    id: 4,
    country: "Russia",
    description: "Tuition from ₽150,000/year | MSU #87 globally | 15,000 govt scholarships",
    image: "https://images.unsplash.com/photo-1647610172519-9aca4dff53e7",
    alt: "Saint Basil Cathedral in Moscow Red Square with vibrant colorful domes",
    universities: "75+",
    students: "5,800+"
  },
  {
    id: 5,
    country: "China",
    description: "PKU #17 & Tsinghua #20 globally | CSC Scholarship 50,000+ awards",
    image: "https://images.unsplash.com/photo-1644546971385-83fa1d539a3e",
    alt: "Great Wall of China winding through lush green mountains under blue sky",
    universities: "95+",
    students: "12,000+"
  },
  {
    id: 6,
    country: "Japan",
    description: "UTokyo #28 globally | MEXT Scholarship | ¥535,800/year national tuition",
    image: "https://images.unsplash.com/photo-1710086617420-15f4d2b20072",
    alt: "Mount Fuji with pink cherry blossoms and traditional Japanese pagoda",
    universities: "65+",
    students: "4,500+"
  },
  {
    id: 7,
    country: "Vietnam",
    description: "Tuition from ₫20M/year | Living costs ₫7M–₫16M/month | 6-7% GDP growth",
    image: "https://images.unsplash.com/photo-1707292098561-a251b9aa4014",
    alt: "Ha Long Bay with emerald green waters and limestone karsts under sunny sky",
    universities: "45+",
    students: "2,800+"
  },
  {
    id: 8,
    country: "Philippines",
    description: "English-medium | WHO-recognized medical degrees | Tuition from ₱30,000/year",
    image: "https://images.unsplash.com/photo-1715314681958-e6d991903e17",
    alt: "Tropical beach in Philippines with turquoise water and white sand",
    universities: "55+",
    students: "3,500+"
  },
  {
    id: 9,
    country: "Saudi Arabia",
    description: "KFUPM #186 globally | SACM fully-funded scholarships | Vision 2030",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15e249787-1772194398623.png",
    alt: "Modern Riyadh skyline with Kingdom Centre Tower illuminated at dusk",
    universities: "40+",
    students: "2,100+"
  },
  {
    id: 11,
    country: "South Korea",
    description: "SNU #31 & KAIST #47 globally | KGSP Scholarship | ₩900,000/month",
    image: "https://images.unsplash.com/photo-1704814490794-b276010c816c",
    alt: "Seoul colorful cityscape with Namsan Tower and vibrant buildings",
    universities: "50+",
    students: "3,800+"
  },
  {
    id: 12,
    country: "Ireland",
    description: "TCD #81 globally | Google, Apple, Meta HQs | 2-year post-study visa",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1be5585fc-1772113931761.png",
    alt: "Cliffs of Moher in Ireland with dramatic Atlantic Ocean views and lush green coastal landscape",
    universities: "20+",
    students: "3,200+"
  }];



  return (
    <section className={`py-16 md:py-24 bg-background ${className}`}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Explore Study Destinations
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover world-class education opportunities across the globe. Choose from our top destinations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {destinations.map((destination) =>
          <div
            key={destination.id}
            className="group relative overflow-hidden rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-2">

              <div className="relative h-64 overflow-hidden">
                <AppImage
                src={destination.image}
                alt={destination.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-2xl font-bold mb-2">{destination.country}</h3>
                <p className="text-sm mb-4 opacity-90">{destination.description}</p>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="AcademicCapIcon" size={20} className="text-white" />
                    <span className="text-sm font-medium">{destination.universities} Universities</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="UserGroupIcon" size={20} className="text-white" />
                    <span className="text-sm font-medium">{destination.students} Students</span>
                  </div>
                </div>

                <Link
                href="/destinations"
                className="inline-flex items-center space-x-2 text-sm font-semibold text-white hover:text-primary transition-colors duration-300">
                  <span>Learn More</span>
                  <Icon name="ArrowRightIcon" size={16} className="text-current" />
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-12">
          <Link
            href="/destinations"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent/90 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
            <span>View All Destinations</span>
            <Icon name="ArrowRightIcon" size={20} className="text-current" />
          </Link>
        </div>
      </div>
    </section>);

};

export default DestinationSection;